<template>
    <v-container fluid>
        <v-card class="card-format">
            <div class="card-section-header px-6 py-4">
                <div class="d-flex align-center flex-wrap" style="gap:10px;">
                    <v-text-field placeholder="Search" v-model="search" dense outlined hide-details
                        prepend-inner-icon="mdi-magnify" clearable style="width:260px;" class="header-filter-input">
                    </v-text-field>
                    <v-select :items="getEventTypes" item-text="name" item-value="name" placeholder="All Types"
                        v-model="filter_type" dense outlined hide-details clearable style="width:140px;"
                        class="header-filter-input">
                    </v-select>
                    <v-select :items="getLocation" placeholder="All Locations" v-model="filter_location"
                        item-text="name" item-value="id" dense outlined hide-details clearable
                        style="width:170px;" class="header-filter-input">
                    </v-select>
                    <v-autocomplete :items="contributorFilterOptions" :filter="filterContributorOptions"
                        placeholder="All Contributors" v-model="filter_contributor_id" item-text="name"
                        item-value="id" dense outlined hide-details clearable style="width:220px;"
                        class="header-filter-input">
                        <template v-slot:selection="{ item }">
                            <span>{{ item.name }}</span>
                        </template>
                        <template v-slot:item="{ item }">
                            <span>{{ item.name }}</span>
                            <v-chip x-small class="ml-2" color="#3988AA" text-color="white"
                                v-if="item.role">{{ item.role.name }}</v-chip>
                        </template>
                    </v-autocomplete>
                    <v-checkbox v-model="only_missing" hide-details dense class="mt-0 pt-0 header-checkbox">
                        <template v-slot:label>
                            <span style="font-size:13px;color:white;">Only Missing Details</span>
                        </template>
                    </v-checkbox>
                    <v-spacer></v-spacer>
                    <v-btn outlined depressed class="events-header-btn header-outline-btn" @click="fetchData()"
                        :loading="loading">
                        <v-icon left>mdi-refresh</v-icon>
                        Refresh
                    </v-btn>
                </div>
                <div v-if="hasActiveFilters" class="header-clear-row">
                    <v-btn x-small text class="header-clear-btn" @click="clearFilters()">
                        <v-icon left x-small>mdi-close</v-icon>
                        Clear filters
                    </v-btn>
                </div>
            </div>
        </v-card>

        <v-skeleton-loader v-if="loading" type="card, card, card" class="mt-4"></v-skeleton-loader>

        <template v-else>
            <div v-if="eventGroups.length === 0" class="empty-state">
                <v-icon size="48" color="#B9C4CA">mdi-map-marker-check-outline</v-icon>
                <div class="mt-3">
                    {{ only_missing ? 'No bookings are missing venue details — nice work!' : 'No booked events found.' }}
                </div>
            </div>

            <v-card v-for="group in eventGroups" :key="group.event.id" class="group-card mt-4">
                <div class="group-header pa-4">
                    <div class="d-flex justify-space-between align-start flex-wrap" style="gap:16px;">
                        <div class="d-flex flex-wrap" style="gap:28px;flex:1;min-width:0;">
                            <div class="info-item">
                                <div class="info-label">EVENT</div>
                                <div class="info-value">
                                    {{ group.event.name }}
                                    <br>
                                    <small style="color:#3988AA;">{{ group.event.reference_code }}</small>
                                </div>
                            </div>
                            <div class="info-item">
                                <div class="info-label">STATUS</div>
                                <div class="info-value">
                                    <v-chip x-small :color="statusColor(group.event.event_status)" text-color="white"
                                        class="font-weight-600">{{ group.event.event_status }}</v-chip>
                                </div>
                            </div>
                            <div class="info-item">
                                <div class="info-label">TYPE</div>
                                <div class="info-value">
                                    <v-chip x-small label v-if="group.event.lead.event_type"
                                        :color="eventTypeColor(group.event.lead.event_type.name)"
                                        :text-color="eventTypeTextColor(group.event.lead.event_type.name)">
                                        {{ group.event.lead.event_type.name }}
                                    </v-chip>
                                </div>
                            </div>
                            <div class="info-item">
                                <div class="info-label">ORGANISER</div>
                                <div class="info-value">
                                    {{ group.event.lead.first_name }} {{ group.event.lead.last_name }}
                                    <br>
                                    <small style="color:#7C7C7C;">{{ group.event.lead.email }}</small>
                                </div>
                            </div>
                            <div class="info-item">
                                <div class="info-label">EVENT DATE</div>
                                <div class="info-value">{{ $date(group.event.lead.start_date).format('DD/MM/YYYY') }}</div>
                            </div>
                            <div class="info-item" v-if="group.contributors.length">
                                <div class="info-label">CONTRIBUTORS</div>
                                <div class="info-value">
                                    <div v-for="contributor in group.contributors" :key="contributor.id">
                                        {{ contributor.user?.name }}
                                        <v-chip x-small class="ml-1" color="#3988AA" text-color="white"
                                            v-if="contributor.user?.role">{{ contributor.user.role.name }}</v-chip>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="d-flex align-center" style="gap:10px;">
                            <v-btn small dark color="#3988AA" class="custom-buttons" depressed
                                @click="manageEvent(group.event)">Manage</v-btn>
                        </div>
                    </div>

                    <v-divider class="my-4"></v-divider>

                    <div class="d-flex flex-wrap align-center" style="gap:28px;">
                        <div class="stat-item">
                            <div class="stat-value">{{ group.builds.length }}</div>
                            <div class="stat-label">Booking{{ group.builds.length == 1 ? '' : 's' }} Shown</div>
                        </div>
                        <div class="stat-item">
                            <div class="stat-value" :style="group.missingCount > 0 ? 'color:#E74C3C;' : ''">{{ group.missingCount }}</div>
                            <div class="stat-label">Missing Details</div>
                        </div>
                    </div>
                </div>

                <v-divider></v-divider>

                <v-simple-table class="venue-table">
                    <template v-slot:default>
                        <thead>
                            <tr>
                                <th>Supplier</th>
                                <th>Service</th>
                                <th>Venue</th>
                                <th>Address</th>
                                <th>Time</th>
                                <th>Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="build in group.builds" :key="build.id">
                                <td>{{ build.supplier ? build.supplier.supplier_name : '—' }}</td>
                                <td>{{ build.option ? build.option.name : '—' }}</td>
                                <td>
                                    <v-text-field v-model="build.venue" @change="saveBuildField(build, 'venue')"
                                        dense outlined hide-details placeholder="Venue" class="venue-input">
                                    </v-text-field>
                                </td>
                                <td>
                                    <v-text-field :value="addressValue(build)" @change="onAddressChange(build, $event)"
                                        dense outlined hide-details placeholder="Address" class="venue-input">
                                    </v-text-field>
                                </td>
                                <td>
                                    <v-text-field v-model="build.start_time" @change="saveBuildField(build, 'start_time')"
                                        dense outlined hide-details placeholder="HH:MM" style="max-width:120px;"
                                        class="venue-input">
                                    </v-text-field>
                                </td>
                                <td>
                                    <v-chip x-small :color="isBuildMissing(build) ? '#FDECEC' : '#E9F7EF'"
                                        :text-color="isBuildMissing(build) ? '#C0392B' : '#1E8449'"
                                        class="font-weight-600">
                                        {{ isBuildMissing(build) ? 'Missing' : 'Complete' }}
                                    </v-chip>
                                </td>
                            </tr>
                        </tbody>
                    </template>
                </v-simple-table>
            </v-card>
        </template>
    </v-container>
</template>

<script>
import { mapActions, mapGetters } from "vuex"
import _ from 'lodash'
import { resolveItineraryRows } from "@/utils/itineraryRows"
import { dedupeLinkedBuilds } from "@/utils/linkedBuilds"
import { eventStatusColor } from "@/utils/eventStatus"
import pageBadgesMixin from "@/common/PageNameStore/pageBadgesMixin"
import { lookups, loadLookups } from "@/common/companyLookups"

// Matches the quote builder's wizard step order (CustomQuote.vue's build_index_supplier_types)
// so same-day services appear in the same order the customer built them in. Accommodation
// (type_id 1) is pinned first wherever this is used as a sort key, since it spans the whole
// party rather than sitting on one day.
const BUILD_TYPE_ORDER = [1, 2, 6, 8, 4, 5, 3, 7]
const accommodationFirst = (build) => build.type_id === 1 ? 0 : 1

export default {
    mixins: [pageBadgesMixin],
    data: () => ({
        loading: true,
        events: [],
        search: null,
        filter_type: null,
        filter_location: null,
        filter_contributor_id: null,
        only_missing: false,
    }),
    created() {
        this.setPageName('Venue Details')
    },
    mounted() {
        this.fetchData()
        loadLookups('event_types', 'advisors', 'locations')
    },
    computed: {
        getLocation() {
            return lookups.locations
        },
        getEventTypes() {
            return lookups.event_types
        },
        getAdvisors() {
            return lookups.advisors
        },
        ...mapGetters({
            get_selected_company: 'auth/get_selected_company',
        }),
        hasActiveFilters() {
            return !!(this.search || this.filter_type || this.filter_location || this.filter_contributor_id || this.only_missing)
        },
        contributorFilterOptions() {
            return (this.getAdvisors || [])
                .filter(u => u.role?.id !== 1 && (u.access || []).some(a => a.company_id === this.get_selected_company.id))
                .sort((a, b) => (a.role?.id ?? Infinity) - (b.role?.id ?? Infinity))
        },
        totalMissingBuilds() {
            return this.eventGroups.reduce((sum, g) => sum + g.missingCount, 0)
        },
        pageBadges() {
            const events = this.eventGroups.length
            const missing = this.totalMissingBuilds
            return [
                `${events} event${events == 1 ? '' : 's'}`,
                `${missing} missing detail${missing == 1 ? '' : 's'}`,
            ]
        },
        eventGroups() {
            const search = (this.search || '').toLowerCase().trim()

            const groups = this.events.map(event => {
                const allBuilds = this.combinedBuilds(event)
                const missingCount = allBuilds.filter(b => this.isBuildMissing(b)).length
                const builds = this.only_missing ? allBuilds.filter(b => this.isBuildMissing(b)) : allBuilds
                return {
                    event,
                    builds,
                    missingCount,
                    contributors: this.sortedContributors(event),
                }
            }).filter(g => g.builds.length > 0)

            const filtered = groups.filter(g => {
                const event = g.event
                if (this.filter_type && event.lead?.event_type?.name !== this.filter_type) return false
                if (this.filter_location && event.lead?.location?.id !== this.filter_location) return false
                if (this.filter_contributor_id && !(event.contributors || []).some(c => c.user_id === this.filter_contributor_id)) return false
                if (search) {
                    const haystack = [
                        event.name, event.reference_code, event.lead?.first_name, event.lead?.last_name, event.lead?.email
                    ].filter(Boolean).join(' ').toLowerCase()
                    if (!haystack.includes(search)) return false
                }
                return true
            })

            filtered.sort((a, b) => {
                const aDate = a.event.lead?.start_date ? new Date(a.event.lead.start_date).getTime() : Infinity
                const bDate = b.event.lead?.start_date ? new Date(b.event.lead.start_date).getTime() : Infinity
                return aDate - bDate
            })

            return filtered
        },
    },
    methods: {
        ...mapActions({
            setPageName: 'pageNameStore/setPageNameAction',
        }),
        async fetchData() {
            this.loading = true
            try {
                const response = await this.$axios.get(`admin/events/get_venue_details/${this.get_selected_company.id}`)
                const result = response.data
                this.events = result.response ? result.data : []
            } catch (_) {
                this.events = []
            }
            this.loading = false
        },
        clearFilters() {
            this.search = null
            this.filter_type = null
            this.filter_location = null
            this.filter_contributor_id = null
            this.only_missing = false
        },
        filterContributorOptions(item, queryText) {
            const query = queryText.toLowerCase()
            const name = (item.name || '').toLowerCase()
            const role = (item.role?.name || '').toLowerCase()
            return name.includes(query) || role.includes(query)
        },
        sortedContributors(event) {
            return [...(event.contributors || [])].sort((a, b) => (a.user?.role?.id ?? Infinity) - (b.user?.role?.id ?? Infinity))
        },
        combinedBuilds(event) {
            // Keep direct references to the real build objects (no spreading) so edits made
            // via v-model/$set here mutate the same objects eventGroups reads from — a
            // spread copy would be discarded next time eventGroups recomputes (e.g. when
            // toggling Only Missing Details), silently reverting unsaved-looking input.
            let combined_builds = []
            ;(event.quotes || []).forEach(quote => {
                combined_builds = combined_builds.concat(quote.builds || [])
            })
            // One build per supplier booking; copies on variant quotes are kept in sync by the API.
            return _.orderBy(dedupeLinkedBuilds(combined_builds), [accommodationFirst, 'start_date', (build) => BUILD_TYPE_ORDER.indexOf(build.type_id)], ['asc', 'asc', 'asc'])
        },
        addressRow(build) {
            return resolveItineraryRows(build).find(row => row.key === 'address')
        },
        addressValue(build) {
            return this.addressRow(build)?.value || ''
        },
        isBuildMissing(build) {
            return !build.venue || !this.addressValue(build) || !(build.start_time && build.start_time.trim())
        },
        eventTypeColor(name) {
            return name === 'Hen' ? 'pink lighten-4' : name === 'Birthday' ? 'green lighten-4' : name === 'Party' ? 'orange lighten-4' : 'blue lighten-4'
        },
        eventTypeTextColor(name) {
            return name === 'Hen' ? 'pink darken-3' : name === 'Birthday' ? 'green darken-3' : name === 'Party' ? 'orange darken-3' : 'blue darken-3'
        },
        statusColor(status) {
            return eventStatusColor(status)
        },
        manageEvent(event) {
            this.$router.push({ name: 'ManageEvent', params: { event_id: event.id } })
        },
        onAddressChange(build, newValue) {
            const trimmed = (newValue || '').trim()
            const defaultValue = this.addressRow(build)?.defaultValue
            const settings = { ...(build.itinerary_row_settings || {}) }
            const rowSetting = { ...(settings.address || {}) }
            if (!trimmed || trimmed === (defaultValue || '')) {
                delete rowSetting.value
            } else {
                rowSetting.value = trimmed
            }
            if (Object.keys(rowSetting).length === 0) {
                delete settings.address
            } else {
                settings.address = rowSetting
            }
            this.$set(build, 'itinerary_row_settings', settings)
            this.saveRowSettings(build)
        },
        async saveRowSettings(build) {
            try {
                await this.$axios.patch(`admin/quotes/quote_builds/${build.id}`, {
                    itinerary_row_settings: build.itinerary_row_settings,
                })
            } catch (error) {
                this.$toast.error('Error saving address')
                console.error('Failed to save itinerary row settings:', error)
            }
        },
        async saveBuildField(build, field) {
            try {
                const response = await this.$axios.patch(`admin/quotes/quote_builds/${build.id}`, {
                    [field]: build[field],
                })
                if (response.data?.response == true) {
                    this.$toast.success('Saved')
                } else {
                    this.$toast.error('Failed to save')
                }
            } catch (error) {
                this.$toast.error('Error saving')
                console.error(error)
            }
        },
    },
}
</script>

<style lang="scss" scoped>
.card-format {
    border-radius: 10px !important;
    border: 1px solid #E9E9E9 !important;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06) !important;
}

.card-section-header {
    background: linear-gradient(135deg, #3988AA 0%, #2a6a88 100%);
}

.card-section-title {
    font-family: Manrope;
    font-size: 16px;
    font-weight: 700;
    color: white;
}

.header-filter-input ::v-deep .v-input__control {
    background: white;
    border-radius: 4px;
}

.header-checkbox ::v-deep .v-icon {
    color: white !important;
}

.header-clear-row {
    margin: 6px 0 -8px -8px;
}

.header-clear-btn {
    font-family: Manrope;
    font-weight: 600 !important;
    font-size: 12px !important;
    text-transform: none;
    color: white !important;
}

.header-outline-btn {
    background: rgba(255, 255, 255, 0.15) !important;
    color: white !important;
    border-color: rgba(255, 255, 255, 0.5) !important;
}

.events-header-btn {
    font-family: Manrope;
    font-weight: 600 !important;
    font-size: 13px !important;
    text-transform: none;
    height: 36px !important;
    padding: 0 14px !important;
}

.empty-state {
    text-align: center;
    color: #7C7C7C;
    font-family: Manrope;
    padding: 60px 0;
}

.group-card {
    border-radius: 12px !important;
    overflow: hidden;
    border: 1px solid #E9E9E9 !important;
    box-shadow: 5px 5px 13px 0px rgba(240, 240, 240, 0.90), -5px -5px 10px 0px rgba(255, 255, 255, 0.90) !important;
    margin-bottom: 20px;
}

.group-header {
    background-color: #FAFAFA;
}

.info-item {
    min-width: 100px;
}

.info-label {
    font-size: 10px;
    font-weight: 700;
    color: #9E9E9E;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    margin-bottom: 2px;
}

.info-value {
    font-family: Manrope;
    font-size: 13px;
    font-weight: 600;
    color: #101010;
    line-height: 1.5;
}

.stat-item {
    text-align: left;
}

.stat-value {
    font-family: Manrope;
    font-size: 18px;
    font-weight: 700;
    color: #101010;
}

.stat-label {
    font-size: 11px;
    font-weight: 600;
    color: #9E9E9E;
    text-transform: uppercase;
    letter-spacing: 0.04em;
}

.venue-table {
    ::v-deep th {
        font-family: Manrope;
        font-size: 11px !important;
        font-weight: 700 !important;
        color: #9E9E9E !important;
        text-transform: uppercase;
        letter-spacing: 0.04em;
    }

    ::v-deep td {
        font-family: Manrope;
        font-size: 13px;
        vertical-align: middle;
        padding-top: 8px;
        padding-bottom: 8px;
    }
}

.venue-input ::v-deep .v-input__control {
    min-width: 160px;
}

:deep(.custom-buttons span) {
    text-transform: none;
    font-family: Manrope;
    font-size: 13px !important;
    font-weight: 600 !important;
    box-shadow: none !important;
}
</style>
